import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type GoodsReceiptsFields = {
  /**
   * Required for commit form
   */
  controlType?: GoodsReceiptsPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: GoodsReceiptsPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: GoodsReceiptsPlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: GoodsReceiptsPlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: GoodsReceiptsPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: GoodsReceiptsPlaceHolderTypeVal;
  /**
   * Shows if it should be visible by default in forms
   */
  visible?: GoodsReceiptsPlaceHolderTypeVal;
};

export type GoodsReceiptsPlaceHolderTypeVal = {
  createForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
  nestedBuyerForm?: boolean | number | string;
  nestedForm?: boolean | number | string; //use nested<component>form for specific Component
  nestedNotificationForm?: boolean | number | string;
};

export enum EnumGoodsReceiptsFieldStructure {
  GoodsReceiptsTable = 0,
  GoodReceiptsForm = 1,
}

export class GoodsReceiptsList {
  fields: MappedField[];
  userRightFields!: string[];
  hasField: (
    field: GoodsReceiptsFields,
    refId: keyof GoodsReceiptsPlaceHolderTypeVal,
    actualId: keyof GoodsReceiptsPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumGoodsReceiptsFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  validFunWithUserFields = (
    field: GoodsReceiptsFields,
    refId: keyof GoodsReceiptsPlaceHolderTypeVal,
    actualId: keyof GoodsReceiptsPlaceHolderTypeVal,
  ): boolean => {
    if (
      this.userRightFields &&
      !(field.isRequired && field.isRequired[actualId]) //For adding fields not present in default fields
    ) {
      // return this.userRightFields.includes((<string>field.id[refId]).toLowerCase());
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      );
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };

  validFunWithoutUserFields = (
    field: GoodsReceiptsFields,
    refId: keyof GoodsReceiptsPlaceHolderTypeVal,
    actualId: keyof GoodsReceiptsPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  getDefaultFields(type?: EnumGoodsReceiptsFieldStructure): MappedField[] {
    switch (type) {
      case EnumGoodsReceiptsFieldStructure.GoodsReceiptsTable:
        return this.getDatagridFields();
      default:
        return this.getDatagridFields();
    }
  }

  getDatagridFields(): MappedField[] {
    return this.goodsReceiptsList
      .filter((field: GoodsReceiptsFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: GoodsReceiptsFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly goodsReceiptsList: GoodsReceiptsFields[] = [
    // {
    //   id: { datagrid: 'actions' },
    //   headerText: { datagrid: 'Actions' },
    //   dataType: { datagrid: 'actions' },
    //   isRequired: { datagrid: true },
    //   visible: { datagrid: true },
    // },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Plant' },
      id: { datagrid: 'plant' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Part Number' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Purchase Order Item' },
      id: { datagrid: 'purchaseOrderItem' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Material Description' },
      id: { datagrid: 'materialDescription' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'number' },
      headerText: { datagrid: 'Received Quantity' },
      id: { datagrid: 'receivedQuantity' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Received Date' },
      id: { datagrid: 'receiveDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'External Id' },
      id: { datagrid: 'externalId' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Delivery Note' },
      id: { datagrid: 'deliveryNote' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Goods Receive Number' },
      id: { datagrid: 'goodsReceiveNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Goods Receive Item' },
      id: { datagrid: 'goodsReceiveItem' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Purchase Order Number' },
      id: { datagrid: 'purchaseOrderNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Profit Center' },
      id: { datagrid: 'profitCenter' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Remark' },
      id: { datagrid: 'remark' },
      visible: { datagrid: true },
    },
  ];
}
